import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Calendar } from "../components/ui/calendar";
import { toast } from "sonner";
import {
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  Users,
  Clock,
  Loader2,
  Plus,
  MapPin
} from "lucide-react";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths, parseISO, isAfter, startOfDay } from "date-fns";
import { fr } from "date-fns/locale";

const CalendarPage = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [eventsRes, meetingsRes] = await Promise.all([
        axios.get(`${API}/calendar/events`),
        axios.get(`${API}/meetings`)
      ]);
      setEvents(eventsRes.data);
      setMeetings(meetingsRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des événements");
    } finally {
      setIsLoading(false);
    }
  };

  const getEventsForDate = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return events.filter(event => {
      if (event.start === event.end) {
        return event.start === dateStr;
      }
      return dateStr >= event.start && dateStr <= event.end;
    });
  };

  const getEventTypeIcon = (type) => {
    const icons = {
      leave: CalendarDays,
      meeting: Users,
      absence: Clock
    };
    return icons[type] || CalendarDays;
  };

  const getEventTypeLabel = (type) => {
    const labels = {
      leave: "Congé",
      meeting: "Réunion",
      absence: "Absence"
    };
    return labels[type] || type;
  };

  const getEventTypeBadgeColor = (type) => {
    const colors = {
      leave: "bg-green-100 text-green-800 border-green-200 dark:bg-green-900/40 dark:text-green-300 dark:border-green-700/50",
      meeting: "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700/50",
      absence: "bg-red-100 text-red-800 border-red-200 dark:bg-red-900/40 dark:text-red-300 dark:border-red-700/50"
    };
    return colors[type] || "bg-gray-100 text-gray-800 dark:bg-[#112240] dark:text-[#7EA8CE]";
  };

  const selectedDateEvents = getEventsForDate(selectedDate);

  const daysWithEvents = events.reduce((acc, event) => {
    const start = new Date(event.start);
    const end = new Date(event.end);
    const days = eachDayOfInterval({ start, end });
    days.forEach(day => {
      const key = format(day, "yyyy-MM-dd");
      if (!acc[key]) acc[key] = [];
      acc[key].push(event);
    });
    return acc;
  }, {});

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="calendar-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-['Playfair_Display'] text-xl md:text-3xl font-bold text-[#111827] dark:text-[#D6E4F5]">
            Calendrier & Réunions
          </h1>
          <p className="text-[#4B5563] dark:text-[#7EA8CE] mt-1 text-sm md:text-base">
            Visualisez les congés, absences et réunions
          </p>
        </div>
        <Button
          className="bg-[#1A4D2E] hover:bg-[#143D24] dark:bg-[#166534] dark:hover:bg-[#14532d] rounded-full self-start sm:self-auto"
          onClick={() => navigate("/meetings")}
        >
          <Plus className="w-4 h-4 mr-2" />
          Gérer les réunions
        </Button>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-green-500"></div>
          <span className="text-sm text-[#4B5563] dark:text-[#7EA8CE]">Congés</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-blue-500"></div>
          <span className="text-sm text-[#4B5563] dark:text-[#7EA8CE]">Réunions</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-red-500"></div>
          <span className="text-sm text-[#4B5563] dark:text-[#7EA8CE]">Absences</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <Card className="lg:col-span-2 card-neumorphic">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="font-['Playfair_Display'] text-xl dark:text-[#D6E4F5]">
              {format(currentMonth, "MMMM yyyy", { locale: fr })}
            </CardTitle>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                data-testid="prev-month"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                data-testid="next-month"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-2">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              month={currentMonth}
              onMonthChange={setCurrentMonth}
              locale={fr}
              className="w-full border-0"
              classNames={{
                caption: "hidden",
                month: "space-y-2 w-full",
                head_row: "flex w-full",
                head_cell: "text-muted-foreground rounded-md flex-1 font-normal text-[0.8rem] text-center",
                row: "flex w-full mt-1",
                cell: "flex-1 relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected])]:rounded-md",
                day: "h-12 w-full p-0 font-normal aria-selected:opacity-100 rounded-md hover:bg-accent hover:text-accent-foreground focus-visible:outline-none",
                day_selected: "bg-[#1A4D2E] text-white hover:bg-[#1A4D2E] hover:text-white focus:bg-[#1A4D2E] focus:text-white",
                day_today: "bg-accent text-accent-foreground font-semibold",
                day_outside: "text-muted-foreground opacity-40",
              }}
              modifiers={{
                hasEvents: (date) => {
                  const key = format(date, "yyyy-MM-dd");
                  return !!daysWithEvents[key]?.length;
                }
              }}
              modifiersClassNames={{
                hasEvents: "bg-[#E8F5E9] dark:bg-[#1A4D2E]/30 font-medium"
              }}
              components={{
                DayContent: ({ date }) => {
                  const key = format(date, "yyyy-MM-dd");
                  const dayEvents = daysWithEvents[key] || [];
                  return (
                    <div className="relative w-full h-full flex flex-col items-center justify-center">
                      <span>{date.getDate()}</span>
                      {dayEvents.length > 0 && (
                        <div className="flex gap-0.5 mt-1">
                          {dayEvents.some(e => e.type === "leave") && (
                            <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                          )}
                          {dayEvents.some(e => e.type === "meeting") && (
                            <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                          )}
                          {dayEvents.some(e => e.type === "absence") && (
                            <div className="w-1.5 h-1.5 rounded-full bg-red-500" />
                          )}
                        </div>
                      )}
                    </div>
                  );
                }
              }}
            />
          </CardContent>
        </Card>

        {/* Events for selected date */}
        <Card className="card-neumorphic">
          <CardHeader className="pb-2">
            <CardTitle className="font-['Playfair_Display'] text-xl dark:text-[#D6E4F5]">
              {format(selectedDate, "d MMMM yyyy", { locale: fr })}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {selectedDateEvents.length === 0 ? (
              <div className="text-center py-8 text-[#9CA3AF] dark:text-[#3D607E]">
                <CalendarDays className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Aucun événement ce jour</p>
              </div>
            ) : (
              <div className="space-y-3">
                {selectedDateEvents.map((event, index) => {
                  const Icon = getEventTypeIcon(event.type);
                  return (
                    <div
                      key={`${event.id}-${index}`}
                      className="p-4 rounded-xl bg-gray-50 dark:bg-[#112240] border border-gray-100 dark:border-[#1A3050] hover:bg-gray-100 dark:hover:bg-[#1A3050]/60 transition-all"
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className="p-2 rounded-lg"
                          style={{ backgroundColor: `${event.color}20` }}
                        >
                          <Icon
                            className="w-4 h-4"
                            style={{ color: event.color }}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className={`${getEventTypeBadgeColor(event.type)} border text-xs`}>
                              {getEventTypeLabel(event.type)}
                            </Badge>
                          </div>
                          <p className="font-medium text-[#111827] dark:text-[#D6E4F5] text-sm">
                            {event.title}
                          </p>
                          {event.start !== event.end && (
                            <p className="text-xs text-[#9CA3AF] dark:text-[#3D607E] mt-1">
                              {event.start} → {event.end}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Réunions à venir */}
      <Card className="card-neumorphic">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="font-['Playfair_Display'] text-xl dark:text-[#D6E4F5]">
            Réunions à venir
          </CardTitle>
          <Button
            variant="outline"
            size="sm"
            className="dark:border-[#1A3050] dark:text-[#4B8BF4] dark:hover:bg-[#112240]"
            onClick={() => navigate("/meetings")}
          >
            Tout gérer
          </Button>
        </CardHeader>
        <CardContent>
          {meetings.filter(m => m.status === "scheduled").length === 0 ? (
            <div className="text-center py-8 text-[#9CA3AF] dark:text-[#3D607E]">
              <Users className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p>Aucune réunion planifiée</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {meetings
                .filter(m => m.status === "scheduled")
                .sort((a, b) => a.date.localeCompare(b.date))
                .slice(0, 6)
                .map(meeting => (
                  <div
                    key={meeting.id}
                    className="p-4 rounded-xl bg-gray-50 dark:bg-[#112240] border border-gray-100 dark:border-[#1A3050] hover:bg-gray-100 dark:hover:bg-[#1A3050]/50 transition-all"
                  >
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex-shrink-0">
                        <Users className="w-4 h-4 text-blue-600 dark:text-[#4B8BF4]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-[#111827] dark:text-[#D6E4F5] text-sm truncate">
                          {meeting.title}
                        </p>
                        <p className="text-xs text-[#4B5563] dark:text-[#7EA8CE] mt-1">
                          {format(parseISO(meeting.date), "d MMM yyyy", { locale: fr })} · {meeting.time}
                        </p>
                        {meeting.location && (
                          <p className="text-xs text-[#9CA3AF] dark:text-[#3D607E] mt-0.5 flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {meeting.location}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CalendarPage;
